import { Component, Input, OnInit } from '@angular/core';

import * as c3 from 'c3';
import * as d3 from 'd3';

import { select } from '@ptl/angular-redux-store';
import { CurrencyPipe } from '@angular/common';
import { DRXGraphAndTableData } from 'src/app/shared/interfaces/drx.interface';
import { Observable } from 'rxjs';
import { DRUG_TOGGLE_OPTIONS } from 'src/app/shared/enums/drug-costs.enum';

@Component({
    selector: 'app-yearly-drug-costs-graph',
    templateUrl: './yearly-drug-costs-graph.component.html',
    styleUrls: ['./yearly-drug-costs-graph.component.scss'],
    standalone: false
})
export class YearlyDrugCostsGraphComponent implements OnInit {
  @Input() yearlyCostDescriptionContent: string;
  @Input() sectionHeading: string;
  defaultHeading: string = 'Total Estimated Costs by Month';
  
  @select(['componentStates', 'DrxServiceData', 'drxGraphData']) graphData$: Observable<DRXGraphAndTableData>;
  @select(['componentStates', 'DrxServiceData', 'drxToggleOption']) retailMailOrderSwitch$: Observable<DRUG_TOGGLE_OPTIONS>;
  retailMailOrderSwitch: DRUG_TOGGLE_OPTIONS;
  showGraphHeader: boolean;

  constructor(
    private currencyPipe: CurrencyPipe
  ) {
    this.graphData$.subscribe((something: any) => {
      if (something) {
        this.retailMailOrderSwitch$.subscribe(value => {
          this.retailMailOrderSwitch = value;
          this.drawGraph(something);
        });
        this.drawGraph(something);
      }
    });

  }

  async ngOnInit() {
  }

  drawGraph(graphData: DRXGraphAndTableData) {

    const monthLabels = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    let donutChart;
    let updatingDonut = false;

    const updateLegend = () => {
      const doNutDoIt = donutChart.data();

      const donutValues = doNutDoIt.map(arc => {
        return arc.values[0].value;
      });

      const activeDonutLabels = doNutDoIt.map(arc => {
        return arc.values[0].id;
      });

      d3.selectAll('#donutchart-' + this.retailMailOrderSwitch + ' .legend').remove();

      // Custom Legend
      d3.select('#donutchart-' + this.retailMailOrderSwitch)
        .insert('div', '.chart')
        .attr('class', 'legend')
        .selectAll('div')
        .data(activeDonutLabels)
        .enter()
        .append('div')
        .attr('data-id', (id: string) => {
          return id;
        })
        .html((id: string, index: number) => {
          return id;
        })
        .each((id, index, htmlNodes) => {
          d3.select(htmlNodes[index])
            .append('div')
            .style('background-color', donutChart.color(id));
          d3.select(htmlNodes[index])
            .append('span')
            .html((nodeId, index2) => {
              return this.currencyPipe.transform(
                donutValues[index],
                'USD',
                'symbol',
                '2.2-2'
              );
            });
        })
        .on('mouseover', id => {
          donutChart.focus(id);
        })
        .on('mouseout', id => {
          donutChart.revert();
        })
        .on('click', id => {
          d3.selectAll('.legend_values').remove();
          d3.select('#values')
            .selectAll('p')
            .data(donutChart.data(id)[0].values)
            .enter()
            .append('p')
            .text((d: HTMLParagraphElement) => {
              // return d.value;
              return d.nodeValue;
            })
            .classed('legend_values', true);
        });
    };

    const generateDonutGraph = (graph) => {
      const graphAnnualTotal = this.currencyPipe.transform(
        graph.barTotals[13],
        'USD',
        'symbol',
        '2.2-2'
      );
      donutChart = c3.generate({
        padding: {
          top: 15,
          right: 1,
          bottom: 15,
          left: 100
        },
        bindto: '#donutchart-' + this.retailMailOrderSwitch,
        // label: {
        //   show: false
        // },
        gauge: {
          expand: false,
          fullCircle: true,
          // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/23526
          // startingAngle: 90,
          width: 35
        },
        size: {
          height: 300
        },
        data: {
          columns: graph.donutAnnual,
          type: 'donut',
          colors: {
            COPAY: '#801ee8',
            PREMIUM: '#fff829',
            GAP: '#29ffcd',
            DEDUCTIBLE: '#29cdff',
            CATASTROPHIC: '#24d61e',
            'MULTIPLE DRUG PHASES': '#ff7919',
            'ESTIMATED MEDICAL': '#195aff',
            TOTAL: '#8dc63f'
          },
          groups: [
            [
              'COPAY',
              'GAP',
              'DEDUCTIBLE',
              'CATASTROPHIC',
              'MULTIPLE DRUG PHASES',
              'ESTIMATED MEDICAL',
              'PREMIUM'
            ]
          ]
        },
        legend: {
          // hidden: true,
          hide: true,
          show: false
        },
        axis: {
          x: {
            type: 'category',
            // transform: {
            //   x: 0,
            //   y: 0
            // }
          },
          y: {
            padding: { top: 250 }
          }
        }
      });

      // Enhanced Title
      d3.select('#donutchart-' + this.retailMailOrderSwitch + ' .c3-chart-arcs')
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('font-size', '24px')
        .attr('y', 0)
        .attr('data-total-value', 'true')
        .attr('class', 'donut-text')
        .text(graphAnnualTotal);

      d3.select('#donutchart-' + this.retailMailOrderSwitch + ' .c3-chart-arcs')
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('y', 22)
        .attr('data-total-label', 'true')
        .attr('class', 'donut-text')
        .text('Total for');

      d3.select('#donutchart-' + this.retailMailOrderSwitch + ' .c3-chart-arcs')
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('y', 40)
        .attr('data-time-label', 'true')
        .attr('class', 'donut-text')
        .text('Year');

      // Create Legend
      updateLegend();
    };

    const updateDonutGraph = (month, value, total) => {
      donutChart.load({
        columns: value,
        unload: true
      });

      // Update Label
      d3.select(
        '#donutchart-' + this.retailMailOrderSwitch + '  text[data-total-value]'
      ).html(total);

      d3.select(
        '#donutchart-' + this.retailMailOrderSwitch + '  text[data-time-label]'
      ).html(month);

      // Update Legend Data
      // Timeout is to avoid race conditions when switching around with mouseon/mouseoff events.
      setTimeout(() => {
        updateLegend();
        updatingDonut = false;
      }, 300);
    };

    const generateBarGraph = (graph) => {
      if (graph.barTotals[0] !== 'Monthly Costs') {
        graph.barTotals.unshift('Monthly Costs');
      }
      c3.generate({
        padding: {
          top: 15,
          right: 1,
          bottom: 15,
          left: 100
        },
        bindto: '#barchart-' + this.retailMailOrderSwitch,
        bar: {
          width: 13
        },
        grid: {
          x: {
            show: true
          },
          y: {
            show: true
          }
        },
        size: {
          height: 500
        },
        data: {
          x: 'labels',
          columns: [
            [
              'labels',
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'May',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Oct',
              'Nov',
              'Dec'
            ],
            graph.barTotals
          ],
          type: 'bar',
          onmouseover: d => {
            updatingDonut = true;
            setTimeout(function () {
              updateDonutGraph(
                monthLabels[d.index],
                graphData[this.retailMailOrderSwitch].donutMonthly[monthLabels[d.index]].breakdown,
                this.currencyPipe.transform(
                  graphData[this.retailMailOrderSwitch].donutMonthly[monthLabels[d.index]].total,
                  'USD',
                  'symbol',
                  '2.2-2'
                )
              );
            }.bind(this), 0);
          },
          onmouseout: d => {
            setTimeout(function () {
              if (!updatingDonut) {
                updateDonutGraph(
                  'Per Year',
                  graphData[this.retailMailOrderSwitch].donutAnnual,
                  this.currencyPipe.transform(
                    graph.barTotals[13],
                    'USD',
                    'symbol',
                    '2.2-2'
                  )
                );
              }
            }.bind(this), 20);
          },
          colors: {
            'Monthly Costs': '#4FC3F7',
            Premium: '#004362',
            Copay: '#000',
            Gap: '#7A8B97',
            Deductible: '#00A4E4',
            Catastrophic: '#0071A9',
            'Multiple Drug Phases': '#FF4500',
            'Estimated Medical Costs': '#98C5DC',
            Total: '#8dc63f'
          },
          groups: [
            [
              'PREMIUM',
              'COPAY',
              'GAP',
              'DEDUCTIBLE',
              'CATASTROPHIC',
              'MULTIPLE DRUG PHASES',
              'ESTIMATED MEDICAL COSTS'
            ],
            ['TOTAL']
          ]
        },
        legend: {
          hide: true
        },
        axis: {
          x: {
            type: 'category',
            // transform: {
            //   x: 0,
            //   y: 0
            // }
          },
          y: {
            padding: { top: 100 },
            label: {
              text: 'Total Costs',
              position: 'outer-middle'
            },
            tick: {
              format: d3.format('$,')
            }
          }
        },
        tooltip: {
          format: {
            title: d => {
              return 'Data ' + d;
            },
            value: value => {
              return this.currencyPipe.transform(
                value as string,
                'USD',
                'symbol',
                '2.2-2'
              );
            }
          }
        }
      });
    };

    setTimeout(function () {
      this.showGraphHeader = false;
      generateBarGraph(graphData[this.retailMailOrderSwitch]);
      generateDonutGraph(graphData[this.retailMailOrderSwitch]);
      this.showGraphHeader = true;
    }.bind(this), 0);
  }
}
